import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  NavigationMenu as NavigationMenuRoot,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  cn,
} from "@mozioinc/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useLogout } from "../hooks";
import { useUserData } from "../hooks/use-user-data";
import { TRAVEL_AGENT_POSITION, type TransNamespace } from "../types";
import { travelAgentQuery } from "../utils/query";
import { getWhiteLabel } from "../utils/white-label";
import AgentProfileSvg from "./assets/agent-profile.svg?react";
import ChevronSvg from "./assets/chevron.svg?react";
import CustomerProfileSvg from "./assets/customer-profile.svg?react";
import MyInformationSvg from "./assets/my-information.svg?react";
import MyTripsSvg from "./assets/my-trips.svg?react";
import PassengerSettingsSvg from "./assets/passenger-settings.svg?react";
import PaymentSettingsSvg from "./assets/payment-settings.svg?react";
import SalesBalanceSvg from "./assets/sales-balance.svg?react";
import SignOutSvg from "./assets/sign-out.svg?react";
import WithdrawalsHistorySvg from "./assets/withdrawals-history.svg?react";
import { PreservedLink } from "./preserved-link";

type NavigationItemProps = {
  to?: string;
  text: string;
  icon?: React.ElementType;
  onClick?: () => void;
};

const NavigationItem: React.FC<NavigationItemProps> = ({
  to,
  text,
  icon: Icon,
  onClick,
}) => {
  const isActive = useRouterState().location.pathname.includes(to ?? "");

  return (
    <NavigationMenuItem>
      <PreservedLink href={to} className="flex w-full" onClick={onClick}>
        <NavigationMenuLink active={isActive} asChild>
          <span>
            {Icon && <Icon className="text-2xl" />}
            {text}
            <ChevronSvg className="ml-auto text-2xl lg:hidden" />
          </span>
        </NavigationMenuLink>
      </PreservedLink>
    </NavigationMenuItem>
  );
};

type Link = {
  text: string;
  to?: string;
  icon?: React.ElementType;
  render?: boolean;
  collapsible?: boolean;
  items?: Link[];
};

type NavigationCollapsibleProps = {
  link: Link;
  onClose?: () => void;
};

const NavigationCollapsible: React.FC<NavigationCollapsibleProps> = ({
  link,
  onClose,
}) => {
  const router = useRouterState();
  const highlightCollapse = link.items?.some((item) =>
    router.location.pathname.includes(item.to ?? ""),
  );

  return (
    <Collapsible className="w-full lg:pr-4">
      <CollapsibleTrigger
        className={cn(
          "flex w-full justify-between p-2",
          highlightCollapse ? "font-bold text-primary" : "text-neutral-800",
        )}
        arrowPosition="right"
      >
        <div className="flex gap-1">
          {link.icon && <link.icon className="text-2xl" />}
          {link.text}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2 pl-8 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
        {link.items?.map((link) =>
          link.render ? (
            <NavigationItem
              key={link.to}
              to={link.to}
              text={link.text}
              icon={link.icon}
              onClick={onClose}
            />
          ) : null,
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

type NavigationMenuProps = {
  onClose?: () => void;
};

const refactorLinks = (links: Link[], lang: string): Link[] => {
  return links.map((l) => ({
    ...l,
    ...(l.to ? { to: `/v2/user${l.to}?locale=${lang}` } : {}),
    ...(l.items ? { items: refactorLinks(l.items, lang) } : {}),
  }));
};

export const NavigationMenu = ({ onClose }: NavigationMenuProps) => {
  const {
    tuiCSAdjustments,
    agentIataAndIdEnabled,
    editableTAWithdrawal,
    editableBookingFee,
    editableCustomerProfiles,
    editableCustomerNotifications,
  } = getWhiteLabel();
  const { t, i18n } = useTranslation<TransNamespace>(
    "transfer:navigation-menu",
  );
  const logout = useLogout();
  const { isLoading: isLoadingUser, isTravelAgent } = useUserData();

  const { data: travelAgent, isLoading: isLoadingTravelAgent } = useQuery({
    queryKey: ["travelAgent"],
    queryFn: travelAgentQuery.getTravelAgent,
    enabled: isTravelAgent,
  });

  if (isLoadingUser || isLoadingTravelAgent) {
    return null;
  }

  const isMasterTravelAgent =
    isTravelAgent && travelAgent?.position === TRAVEL_AGENT_POSITION.MASTER;

  const renderTAAccountProfile =
    !tuiCSAdjustments &&
    travelAgent?.company &&
    (isMasterTravelAgent ||
      !travelAgent.company.payoutToMaster ||
      !travelAgent.company.useMasterCommission ||
      agentIataAndIdEnabled);

  const renderSalesAndEarnings =
    isTravelAgent &&
    editableTAWithdrawal &&
    (isMasterTravelAgent ||
      !travelAgent?.company ||
      !travelAgent?.company.payoutToMaster);

  const renderWithdrawalsHistory =
    isTravelAgent &&
    editableTAWithdrawal &&
    (isMasterTravelAgent ||
      !travelAgent?.company ||
      !travelAgent?.company.payoutToMaster);

  const renderCommissionSettings =
    editableBookingFee &&
    (isMasterTravelAgent || !travelAgent?.company.useMasterCommission);

  const renderWithdrawTo =
    editableTAWithdrawal &&
    (isMasterTravelAgent ||
      !travelAgent?.company ||
      !travelAgent?.company.payoutToMaster);

  const renderCustomerProfiles =
    editableCustomerProfiles && isTravelAgent && !tuiCSAdjustments;

  const renderCustomerNotifications =
    editableCustomerNotifications && isTravelAgent;

  const links: Link[] = [
    {
      text: t("My reservations"),
      to: "/my-reservations",
      icon: MyTripsSvg,
      render: true,
    },
    {
      text: t("Account information"),
      to: "/info",
      icon: MyInformationSvg,
      render: !tuiCSAdjustments,
    },
    {
      text: t("Profile"),
      icon: AgentProfileSvg,
      render: renderTAAccountProfile,
      collapsible: true,
      items: [
        {
          text: t("Travel Agent profile"),
          to: "/travel-agent",
          render: renderTAAccountProfile,
        },
        {
          text: t("Company information"),
          to: "/company-information",
          render: isMasterTravelAgent,
        },
      ],
    },
    {
      text: t("Commission settings"),
      to: "/commission-settings",
      icon: PaymentSettingsSvg,
      render: isTravelAgent && (renderCommissionSettings || renderWithdrawTo),
    },
    {
      text: t("Sales and earnings"),
      to: "/sales-earnings",
      icon: SalesBalanceSvg,
      render: renderSalesAndEarnings,
    },
    {
      text: t("Withdrawal history"),
      to: "/withdrawal-history",
      icon: WithdrawalsHistorySvg,
      render: renderWithdrawalsHistory,
    },
    {
      text: t("Customer profiles"),
      to: "/customer-profiles",
      icon: CustomerProfileSvg,
      render: renderCustomerProfiles,
    },
    {
      text: t("Customer notifications"),
      to: "/customer-notifications",
      icon: PassengerSettingsSvg,
      render: renderCustomerNotifications,
    },
  ];

  return (
    <div className="flex flex-col items-start px-2 lg:px-0">
      <NavigationMenuRoot className="mt-2 flex lg:mt-5">
        <NavigationMenuList className="w-screen pr-10 lg:w-60 lg:pr-0">
          {/* Keyed on `text`, not `to`: every top-level `links` entry has a
              distinct literal `text`, but "Profile" — the only collapsible —
              has no top-level `to`, which made `key={link.to}` undefined. */}
          {refactorLinks(links, i18n.language).map((link) =>
            link.render ? (
              link.collapsible ? (
                <NavigationCollapsible
                  key={link.text}
                  link={link}
                  onClose={onClose}
                />
              ) : (
                <NavigationItem
                  key={link.text}
                  to={link.to}
                  text={link.text}
                  icon={link.icon}
                  onClick={onClose}
                />
              )
            ) : null,
          )}

          <NavigationMenuItem>
            <Link onClick={logout}>
              <NavigationMenuLink className="text-error" asChild>
                <span>
                  <SignOutSvg className="text-2xl" />
                  {t("Log out")}
                </span>
              </NavigationMenuLink>
            </Link>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenuRoot>
    </div>
  );
};
